import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2, Send, Wallet as WalletIcon, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { transactionApi, walletApi } from '@/services/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import type { Contact } from '@/types'
import { useWallets } from '@/hooks/useWallets'
import { formatCurrency } from '@/lib/utils'

interface SendMoneyToContactModalProps {
  isOpen: boolean
  contact: Contact | null
  onClose: () => void
  onSuccess?: () => void
}

export function SendMoneyToContactModal({ 
  isOpen, 
  contact, 
  onClose, 
  onSuccess 
}: SendMoneyToContactModalProps) {
  const { t } = useTranslation()
  const { wallets, refetch } = useWallets()
  const [selectedWalletId, setSelectedWalletId] = useState<string>('')
  const [amount, setAmount] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [isSending, setIsSending] = useState(false)

  // Set default wallet when modal opens
  useEffect(() => {
    if (isOpen && wallets && wallets.length > 0) {
      const defaultWallet = wallets.find(w => w.is_default) || wallets[0]
      if (defaultWallet) {
        setSelectedWalletId(defaultWallet.id)
      }
    }
  }, [isOpen, wallets])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAmount('')
      setDescription('')
      setIsSending(false)
    }
  }, [isOpen])

  const selectedWallet = wallets?.find(w => w.id === selectedWalletId)
  const maxAmount = selectedWallet ? parseFloat(selectedWallet.available_balance.toString()) : 0

  const handleSendMoney = async () => {
    if (!contact) {
      toast.error(t('contacts.selectContact') || 'Por favor, selecione um contacto')
      return
    }

    if (!selectedWalletId || !selectedWallet) {
      toast.error(t('wallet.selectWallet') || 'Por favor, selecione uma carteira')
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }

    if (parseFloat(amount) > maxAmount) {
      toast.error(t('wallet.insufficientBalance') || 'Saldo insuficiente')
      return
    }

    setIsSending(true)

    try {
      // Send money using email or phone
      await transactionApi.sendMoney({
        to_email: contact.email || undefined,
        to_phone: contact.phone_number ? `${contact.country_code}${contact.phone_number}` : undefined,
        from_wallet_id: selectedWalletId,
        amount: parseFloat(amount),
        description: description || t('wallet.sendMoney') || 'Enviar dinheiro',
      })

      toast.success(t('wallet.sendMoneySuccess'))
      
      // Refetch wallets to update balance
      refetch()
      
      // Call success callback
      onSuccess?.()
      
      // Close modal
      onClose()
    } catch (error: any) {
      console.error('Error sending money:', error)
      
      // Check for specific error cases
      const errorResponse = error.response?.data
      const errorText = errorResponse?.error || errorResponse?.detail || ''
      const errorStatus = error.response?.status
      
      let errorMessage = t('wallet.sendMoneyFailed')
      
      // Check if recipient doesn't have a wallet
      if (
        errorText.toLowerCase().includes('recipient has not created a wallet') ||
        errorText.toLowerCase().includes('has not created a wallet yet') ||
        errorText.toLowerCase().includes('recipient has not') ||
        (errorText.toLowerCase().includes('wallet') && errorText.toLowerCase().includes('create') && errorText.toLowerCase().includes('recipient'))
      ) {
        errorMessage = t('wallet.sendMoneyRecipientNoWallet')
      } else if (errorText) {
        // Use the specific error message from the backend if available
        errorMessage = errorText
      } else if (errorStatus === 404) {
        // If it's a 404, it might be a route issue or recipient not found
        // Check if it's a route issue (empty response) vs actual error
        if (!errorText) {
          errorMessage = t('wallet.sendMoneyFailed')
        } else {
          // If there's error text with 404, treat as recipient not found
          errorMessage = t('wallet.sendMoneyRecipientNoWallet')
        }
      }
      
      toast.error(errorMessage)
    } finally {
      setIsSending(false)
    }
  }

  if (!contact) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('wallet.sendMoney') || 'Enviar Dinheiro'}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  disabled={isSending}
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-bold text-lg">
                        {contact.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-text-primary">
                        {contact.name}
                      </h3>
                      {contact.phone_number && (
                        <p className="text-sm text-text-secondary">
                          {contact.country_code} {contact.phone_number}
                        </p>
                      )}
                      {contact.email && (
                        <p className="text-sm text-text-secondary truncate">
                          {contact.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Select Wallet */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('wallet.selectWallet') || 'Selecionar Carteira'}
                  </label>
                  <div className="space-y-2">
                    {wallets && wallets.length > 0 ? (
                      wallets.map((wallet) => (
                        <button
                          key={wallet.id}
                          onClick={() => setSelectedWalletId(wallet.id)}
                          disabled={isSending}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                            selectedWalletId === wallet.id
                              ? 'border-primary bg-primary/5 dark:bg-primary/10'
                              : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <WalletIcon className="h-5 w-5 text-primary" />
                              <div>
                                <p className="font-medium text-text-primary">
                                  {wallet.currency === 'STN' ? 'Dobra (STN)' : 
                                   wallet.currency === 'EUR' ? 'Euro (EUR)' : 
                                   'Dólar (USD)'}
                                </p>
                                <p className="text-sm text-text-secondary">
                                  {formatCurrency(wallet.available_balance, wallet.currency_code || wallet.currency)} disponível
                                </p>
                              </div>
                            </div>
                            {wallet.is_default && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                {t('default') || 'Padrão'}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-text-secondary">
                          {t('wallet.noWallet') || 'Nenhuma carteira disponível'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('wallet.amount') || 'Valor'} *
                  </label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={maxAmount}
                      placeholder="0.00"
                      value={amount}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                      required
                      disabled={isSending || !selectedWallet}
                      className="pr-20"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary">
                      {selectedWallet?.currency === 'STN' ? 'Db' : 
                       selectedWallet?.currency === 'EUR' ? '€' : 
                       '$'}
                    </span>
                  </div>
                  {selectedWallet && (
                    <p className="text-xs text-text-secondary mt-1">
                      {t('wallet.availableBalance') || 'Saldo disponível'}: {formatCurrency(selectedWallet.available_balance, selectedWallet.currency_code || selectedWallet.currency)}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('common.description') || 'Descrição'} ({t('common.optional') || 'Opcional'})
                  </label>
                  <Input
                    type="text"
                    placeholder={t('wallet.paymentDescription') || 'Descrição do pagamento'}
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                    disabled={isSending}
                  />
                </div>

                {/* Actions */}
                <div className="flex space-x-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                    disabled={isSending}
                  >
                    {t('common.cancel') || 'Cancelar'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSendMoney}
                    className="flex-1"
                    disabled={
                      isSending ||
                      !selectedWalletId ||
                      !amount ||
                      parseFloat(amount) <= 0 ||
                      parseFloat(amount) > maxAmount
                    }
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('wallet.sending') || 'A enviar...'}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {t('wallet.send') || 'Enviar'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

